"use client";

import { useQuery } from "@tanstack/react-query";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { usePurchaseAuth } from "@/contexts/purchase-auth-provider";

import {
  ShopCatalogView,
  type ShopCatalogViewInitialState,
  useShopCatalogView,
} from "@/hooks/shop/use-shop-catalog-view";

import { ShopCatalogResponse } from "@/types/shop";

import { shopQueryOptions } from "@/query/shop/shop-query";

const EMPTY_SHOP_CATALOG: ShopCatalogResponse = {
  items: [],
  categories: [],
  promotions: [],
  shipping: null,
};

export type FeaturedShopItemState =
  | { status: "none" }
  | { status: "pending"; itemId: string }
  | {
      status: "ready";
      itemId: string;
      item: ShopCatalogResponse["items"][number];
    }
  | { status: "missing"; itemId: string }
  | { status: "error"; itemId: string };

interface ShopCatalogContextValue {
  catalog: ShopCatalogResponse;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  featured: FeaturedShopItemState;
  promotionFocus?: {
    state: { focusedPromotionId: string | null };
    actions: {
      focusPromotion: (promotionId: string) => void;
      clearPromotionFocus: () => void;
    };
  };
  view?: ShopCatalogView;
  retryFeatured: () => void;
}

const ShopCatalogContext = createContext<ShopCatalogContextValue | null>(null);

interface ShopCatalogProviderProps {
  tenantSlug: string;
  featuredItemId?: string | null;
  initialView?: ShopCatalogViewInitialState;
  children: React.ReactNode;
}

function resolveFeaturedShopItem(
  catalog: ShopCatalogResponse,
  featuredItemId: string | null | undefined,
  isLoading: boolean,
  isError: boolean,
): FeaturedShopItemState {
  if (!featuredItemId) return { status: "none" };
  if (isLoading) return { status: "pending", itemId: featuredItemId };
  if (isError) return { status: "error", itemId: featuredItemId };

  const item = catalog.items.find(
    (candidate) => candidate.id === featuredItemId,
  );
  return item
    ? { status: "ready", itemId: featuredItemId, item }
    : { status: "missing", itemId: featuredItemId };
}

export function useShopPromotionFocus(catalog: ShopCatalogResponse) {
  const [focusedPromotionId, setFocusedPromotionId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (
      focusedPromotionId &&
      !catalog.promotions.some(
        (promotion) => promotion.id === focusedPromotionId,
      )
    ) {
      setFocusedPromotionId(null);
    }
  }, [catalog.promotions, focusedPromotionId]);

  const focusPromotion = useCallback((promotionId: string) => {
    setFocusedPromotionId(promotionId);
  }, []);
  const clearPromotionFocus = useCallback(() => {
    setFocusedPromotionId(null);
  }, []);

  return useMemo(
    () => ({
      state: { focusedPromotionId },
      actions: { focusPromotion, clearPromotionFocus },
    }),
    [clearPromotionFocus, focusPromotion, focusedPromotionId],
  );
}

/**
 * Owns the single catalog subscription for the complete browse-to-checkout
 * flow. The patient id stays in the query identity, so auth changes select a
 * separate catalog without remounting the flow or sharing personalized data.
 */
export function ShopCatalogProvider({
  tenantSlug,
  featuredItemId,
  initialView,
  children,
}: ShopCatalogProviderProps) {
  const { buyer } = usePurchaseAuth();
  const { data, isLoading, isError, error, refetch } = useQuery({
    ...shopQueryOptions.catalog(tenantSlug, buyer?.id),
  });
  const catalog = data ?? EMPTY_SHOP_CATALOG;
  const promotionFocus = useShopPromotionFocus(catalog);
  const view = useShopCatalogView(catalog, initialView);
  const value = useMemo<ShopCatalogContextValue>(() => {
    const featured = resolveFeaturedShopItem(
      catalog,
      featuredItemId,
      isLoading,
      isError,
    );

    return {
      catalog,
      isLoading,
      isError,
      error,
      featured,
      promotionFocus,
      view,
      retryFeatured: () => void refetch(),
    };
  }, [
    catalog,
    error,
    featuredItemId,
    isError,
    isLoading,
    promotionFocus,
    refetch,
    view,
  ]);

  return (
    <ShopCatalogContext.Provider value={value}>
      {children}
    </ShopCatalogContext.Provider>
  );
}

export function useShopCatalog(): ShopCatalogContextValue {
  const context = useContext(ShopCatalogContext);
  if (!context) {
    throw new Error(
      "useShopCatalog must be used inside a <ShopCatalogProvider>",
    );
  }
  return context;
}

export function useFeaturedShopItem() {
  const { featured, retryFeatured } = useShopCatalog();
  return {
    state: { featured },
    actions: { retryFeatured },
  };
}
